import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalBody, ModalFooter, ModalHeader, Button } from 'reactstrap';
import OrgUserService from '../../../services/orgUser.service';
import '../../../styles/dashboard/OrgUser/KycVerification.css';

export default function KycGenericVerification({
  title,
  description,
  kycType,
  costField,
  inputs,
  submitLabel,
  referenceFormatter,
}) {
  const [activeTab, setActiveTab] = useState('form');
  const [formValues, setFormValues] = useState(() => {
    const initial = {};
    inputs.forEach((input) => {
      initial[input.name] = '';
    });
    return initial;
  });
  const [submitError, setSubmitError] = useState('');
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [reportLoadingId, setReportLoadingId] = useState(null);
  const [fetchLoadingId, setFetchLoadingId] = useState(null);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [successDetails, setSuccessDetails] = useState(null);
  const [pollingActive, setPollingActive] = useState(false);
  const [pollingAttempts, setPollingAttempts] = useState(0);
  const [pollingMessage, setPollingMessage] = useState('');

  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const perPage = 10;

  const [filterForm, setFilterForm] = useState({
    search: '',
    fromDate: '',
    toDate: '',
    status: 'all'
  });
  const [filters, setFilters] = useState({
    search: '',
    fromDate: '',
    toDate: '',
    status: 'all'
  });

  const [verificationCost, setVerificationCost] = useState(0);

  // Compute these early so they can be used in useEffect hooks
  const isCourtCase = kycType === 'court-case-check';
  const isDlVerification = kycType === 'dl-verification';
  const showReportButton =
    kycType === 'rc-verification'
    || kycType === 'dl-verification'
    || kycType === 'court-case-check'
    || kycType === 'pan-to-bank-account'
    || kycType === 'mobile-to-bank-account';

  const getErrorMessage = (err, fallback) => {
    const detailsMessage = err?.details?.message || err?.details?.error || '';
    const hint = err?.hint ? ` ${err.hint}` : '';
    return `${err?.message || err?.error || detailsMessage || fallback || 'Request failed'}${hint}`.trim();
  };

  useEffect(() => {
    const fetchCost = async () => {
      try {
        const res = await OrgUserService.getCreditCosts();
        const cost = res?.data?.[costField] ?? 0;
        setVerificationCost(cost);
      } catch (err) {
        console.error('Failed to fetch cost:', err);
      }
    };
    fetchCost();
  }, [costField]);

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const normalizedStatus = filters.status === 'all' ? '' : filters.status;
      const res = await OrgUserService.getKycVerifications(
        kycType,
        currentPage,
        perPage,
        filters.search,
        filters.fromDate,
        filters.toDate,
        normalizedStatus
      );
      const list = res.data || res?.data?.data || [];
      setHistoryItems(list);
      const total = res.pagination?.totalRecords || res.pagination?.total || list.length;
      setTotalRecords(total);
    } catch (err) {
      setHistoryItems([]);
      setTotalRecords(0);
    }
    setHistoryLoading(false);
  };

  useEffect(() => {
    if (activeTab === 'history') {
      loadHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, currentPage, filters]);

  // Auto-polling for in_progress verifications
  useEffect(() => {
    if (!pollingActive || !successDetails?.id) {
      console.log('⏸️ Polling not active or no ID:', { pollingActive, id: successDetails?.id });
      return;
    }

    const maxAttempts = 20;
    const pollInterval = 3000;

    if (pollingAttempts >= maxAttempts) {
      console.log('⏱️ Polling timeout reached');
      setPollingActive(false);
      setPollingMessage('Polling timeout. Please fetch results manually from history.');
      return;
    }

    console.log(`🔄 Scheduling poll attempt ${pollingAttempts + 1}/${maxAttempts} for verification ${successDetails.id}`);

    const timer = setTimeout(async () => {
      try {
        setPollingMessage(`Fetching results... (Attempt ${pollingAttempts + 1}/${maxAttempts})`);
        console.log(`📡 Fetching results for ${isCourtCase ? 'court case' : 'DL'} verification ${successDetails.id}`);
        
        const res = isCourtCase
          ? await OrgUserService.fetchKycCourtCaseResults(successDetails.id)
          : await OrgUserService.fetchKycDlVerificationResults(successDetails.id);
        
        console.log('📥 Fetch response:', res);
        const updatedStatus = res?.status || 'completed';
        setSuccessDetails((prev) => ({ ...prev, status: updatedStatus }));

        if (updatedStatus === 'completed') {
          console.log('✅ Verification completed!');
          setPollingActive(false);
          setPollingMessage('✅ Results fetched successfully!');
          await loadHistory();
        } else if (updatedStatus === 'failed') {
          console.log('❌ Verification failed');
          setPollingActive(false);
          setPollingMessage('❌ Verification failed.');
          await loadHistory();
        } else if (updatedStatus === 'in_progress') {
          console.log('⏳ Still in progress, will retry...');
          setPollingAttempts((prev) => prev + 1);
        }
      } catch (err) {
        console.error('❌ Auto-polling error:', err);
        const errorMessage = String(
          err?.message
          || err?.error
          || err?.details?.message
          || ''
        ).toLowerCase();
        const providerCode = Number(err?.details?.code || 0);
        const isRateLimited = providerCode === 429 || errorMessage.includes('rate limit');

        if (isRateLimited) {
          setPollingActive(false);
          setPollingMessage('Provider rate limit reached. Please wait 1-2 minutes and use Fetch Result from history.');
          return;
        }

        setPollingAttempts((prev) => prev + 1);
      }
    }, pollInterval);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollingActive, pollingAttempts, successDetails?.id, isCourtCase]);

  const handleInputChange = (name, value) => {
    const fieldConfig = inputs.find((input) => input.name === name);
    const nextValue = fieldConfig?.formatter ? fieldConfig.formatter(value) : value;
    setFormValues((prev) => ({ ...prev, [name]: nextValue }));
  };

  const handleFilterChange = (name, value) => {
    setFilterForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
    setFilters(filterForm);
  };

  const handleResetFilters = () => {
    const next = { search: '', fromDate: '', toDate: '', status: 'all' };
    setFilterForm(next);
    setFilters(next);
    setCurrentPage(1);
  };

  const validateForm = () => {
    for (const input of inputs) {
      const value = String(formValues[input.name] || '').trim();
      if ((input.required ?? true) && !value) {
        return input.requiredMessage || `${input.label} is required`;
      }
      if (input.validator && value && !input.validator(value)) {
        return input.errorMessage || `Invalid ${input.label}`;
      }
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitMessage('');

    const validationError = validateForm();
    if (validationError) {
      setSubmitError(validationError);
      return;
    }

    const payload = {};
    inputs.forEach((input) => {
      const rawValue = formValues[input.name];
      payload[input.name] = input.transform ? input.transform(rawValue) : rawValue;
    });

    setSubmitting(true);
    try {
      const res = await OrgUserService.createKycVerification(kycType, payload);
      setSubmitMessage(res?.message || `${title} request submitted. Processing will start soon.`);
      const verificationStatus = res?.data?.status || res?.data?.data?.status || 'completed';
      const verificationId = res?.data?.id || res?.data?.data?.id;
      
      console.log('✅ Verification created:', { verificationId, verificationStatus, kycType, isCourtCase, isDlVerification });
      
      setSuccessDetails({
        id: verificationId,
        status: verificationStatus,
        reference: referenceFormatter
          ? referenceFormatter(res?.data || res?.data?.data || {})
          : payload[inputs[0]?.name]
      });
      
      // Auto-poll for court case only; DL now completes inline on the backend.
      if (verificationStatus === 'in_progress' && isCourtCase) {
        console.log('🔄 Starting auto-polling for', kycType);
        setPollingActive(true);
        setPollingAttempts(0);
        setPollingMessage('Initiating automatic result fetch...');
      } else {
        console.log('⏭️ Skipping polling:', { verificationStatus, isCourtCase, isDlVerification });
        setPollingActive(false);
        setPollingMessage('');
      }
      
      setSuccessModalOpen(true);
      const resetValues = {};
      inputs.forEach((input) => {
        resetValues[input.name] = '';
      });
      setFormValues(resetValues);
      // Always reload history and switch to tab, even if tab was already 'history'.
      setActiveTab('history');
      setCurrentPage(1);
      await loadHistory();
    } catch (err) {
      setSubmitError(getErrorMessage(err, `Failed to submit ${title}`));
    }
    setSubmitting(false);
  };

  const totalPages = Math.ceil(totalRecords / perPage) || 1;
  const formatReference = referenceFormatter || ((item) => item?.[inputs[0]?.name] || 'N/A');

  const handleViewReport = async (verificationId) => {
    setReportLoadingId(verificationId);
    try {
      const res = await OrgUserService.downloadKycVerificationReport(kycType, verificationId);
      const pdfReady = res?.data?.pdfReady ?? res?.data?.data?.pdfReady;
      const url = res?.data?.url || res?.data?.downloadUrl || res?.data?.data?.url || res?.data?.data?.downloadUrl;

      if (!pdfReady) {
        alert(res?.data?.message || 'PDF is still being generated. Please try again shortly.');
        return;
      }

      if (url) {
        window.open(url, '_blank', 'noopener');
      } else {
        alert('Report URL not available yet. Please try again.');
      }
    } catch (err) {
      alert(getErrorMessage(err, 'Failed to download KYC report'));
    } finally {
      setReportLoadingId(null);
    }
  };

  const handleFetchVerificationResult = async (verificationId) => {
    setFetchLoadingId(verificationId);
    setSubmitError('');
    setSubmitMessage('');
    try {
      const existingItem = historyItems.find((item) => item.id === verificationId) || null;
      const res = isCourtCase
        ? await OrgUserService.fetchKycCourtCaseResults(verificationId)
        : await OrgUserService.fetchKycDlVerificationResults(verificationId);
      const message = res?.message || 'Verification updated.';
      const updatedStatus = res?.status || existingItem?.status || 'completed';
      const detailSource = res?.data || existingItem || {};

      setSubmitMessage(message);
      setSuccessDetails({
        id: verificationId,
        status: updatedStatus,
        reference: referenceFormatter
          ? referenceFormatter(detailSource)
          : formatReference(detailSource)
      });
      setPollingActive(false);
      setPollingMessage('');
      setSuccessModalOpen(true);
      await loadHistory();
    } catch (err) {
      setSubmitError(getErrorMessage(err, 'Failed to fetch verification results'));
    } finally {
      setFetchLoadingId(null);
    }
  };

  const handleCloseSuccessModal = async () => {
    setSuccessModalOpen(false);
    setPollingActive(false);
    setPollingMessage('');
    setPollingAttempts(0);
    setSubmitMessage('');
    setSubmitError('');
    setSuccessDetails(null);

    if (activeTab !== 'history') {
      setActiveTab('history');
      setCurrentPage(1);
    }

    await loadHistory();
  };

  return (
    <div className="kyc-page">
      <Modal isOpen={successModalOpen} toggle={handleCloseSuccessModal} className="kyc-success-modal" centered>
        <ModalHeader toggle={handleCloseSuccessModal}>
          KYC Verification {successDetails?.status === 'completed' ? 'Completed' : (successDetails?.status === 'in_progress' ? 'In Progress' : 'Submitted')}
        </ModalHeader>
        <ModalBody>
          <div className="kyc-success-summary">
            <div>
              <div className="kyc-success-name">{title}</div>
              <div className="kyc-success-pan">
                Reference: {successDetails?.reference || 'N/A'}
              </div>
            </div>
          </div>
          <div className="kyc-response">
            <div className="kyc-response-row"><span>Status</span><strong>{successDetails?.status || 'completed'}</strong></div>
            <div className="kyc-response-row"><span>Verification ID</span><strong>{successDetails?.id || 'N/A'}</strong></div>
            <div className="kyc-response-row"><span>Summary</span><strong>KYC verification {successDetails?.status === 'completed' ? 'Completed' : (successDetails?.status === 'in_progress' ? 'Processing' : 'Submitted')}</strong></div>
          </div>
          {pollingMessage && (
            <div style={{ marginTop: '12px', padding: '10px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '4px', fontSize: '13px', color: '#0369a1' }}>
              {pollingMessage}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          {showReportButton && successDetails?.id && successDetails?.status === 'completed' && (
            <Button
              color="primary"
              onClick={() => handleViewReport(successDetails.id)}
              disabled={reportLoadingId === successDetails.id}
            >
              {reportLoadingId === successDetails.id ? 'Loading...' : 'View Report'}
            </Button>
          )}
          <Button color="secondary" onClick={handleCloseSuccessModal} disabled={pollingActive}>
            {pollingActive ? 'Fetching...' : 'Close'}
          </Button>
        </ModalFooter>
      </Modal>
      <div className="kyc-header">
        <div>
          <h1>{title}</h1>
          <p>{description}</p>
        </div>
      </div>

      {isCourtCase && (
        <div style={{
          background: '#fff8e1',
          border: '1px solid #f9a825',
          borderRadius: '6px',
          padding: '10px 14px',
          margin: '0 0 16px 0',
          fontSize: '13px',
          color: '#5f4000',
          lineHeight: '1.5'
        }}>
          <strong>⚠️ Disclaimer:</strong> Court case data is fetched from public judicial records and matched based on name, address, and personal details provided. Due to common names or similar personal details, results may occasionally include cases belonging to different individuals. Please cross-verify all court case findings independently before making any credit or legal decisions.
        </div>
      )}

      <div className="kyc-tabs">
        <button
          className={`kyc-tab-btn ${activeTab === 'form' ? 'active' : ''}`}
          onClick={() => setActiveTab('form')}
        >
          {title}
        </button>
        <button
          className={`kyc-tab-btn ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          History
        </button>
      </div>

      {activeTab === 'form' ? (
        <form className="kyc-card" onSubmit={handleSubmit}>
          <div className="kyc-card-title">{title}</div>
          <p className="kyc-card-desc">
            {description}
            <br />
            <strong>Cost: Rs. {verificationCost}</strong> per request
          </p>

          {inputs.map((input) => (
            <div className="kyc-form-group" key={input.name}>
              <label htmlFor={input.name}>{input.label}</label>
              <input
                id={input.name}
                name={input.name}
                type={input.type || 'text'}
                value={formValues[input.name]}
                onChange={(e) => handleInputChange(input.name, e.target.value)}
                placeholder={input.placeholder}
                maxLength={input.maxLength}
                autoComplete={input.autoComplete || 'off'}
                className="kyc-input"
              />
            </div>
          ))}

          {submitError && <div className="kyc-error">{submitError}</div>}
          {submitMessage && <div className="kyc-success">{submitMessage}</div>}

          <button className="kyc-primary-btn" type="submit" disabled={submitting}>
            {submitting ? 'Submitting...' : submitLabel}
          </button>
        </form>
      ) : (
        <div className="kyc-history">
          <div className="kyc-filters">
            <input
              type="text"
              className="kyc-input"
              placeholder="Search"
              value={filterForm.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
            <input
              type="date"
              className="kyc-input"
              value={filterForm.fromDate}
              onChange={(e) => handleFilterChange('fromDate', e.target.value)}
            />
            <input
              type="date"
              className="kyc-input"
              value={filterForm.toDate}
              onChange={(e) => handleFilterChange('toDate', e.target.value)}
            />
            <select
              className="kyc-input"
              value={filterForm.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>
            <div className="kyc-filter-actions">
              <button type="button" onClick={handleApplyFilters}>
                Apply
              </button>
              <button type="button" onClick={handleResetFilters}>
                Reset
              </button>
            </div>
          </div>
          {submitError && <div className="kyc-error">{submitError}</div>}
          {submitMessage && <div className="kyc-success">{submitMessage}</div>}
          {historyLoading ? (
            <div className="kyc-empty">Loading history...</div>
          ) : (
            <>
              <div className="kyc-table-wrapper">
                <table className="kyc-table">
                  <thead>
                    <tr>
                      <th>Reference</th>
                      <th>Status</th>
                      <th>Cost</th>
                      <th>Created At</th>
                      <th>Report</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyItems.length === 0 ? (
                      <tr>
                        <td colSpan="5">No history available.</td>
                      </tr>
                    ) : (
                      historyItems.map((item) => (
                        <tr key={item.id}>
                          <td>{formatReference(item)}</td>
                          <td>{item.status || 'pending'}</td>
                          <td>Rs. {item.cost ?? '0.00'}</td>
                          <td>{item.createdAt ? new Date(item.createdAt).toLocaleString() : 'N/A'}</td>
                          <td>
                            <div className="kyc-action-group">
                              {item.reportId ? (
                                <button
                                  type="button"
                                  className="kyc-link-btn"
                                  onClick={() => handleViewReport(item.id)}
                                  disabled={reportLoadingId === item.id}
                                >
                                  {reportLoadingId === item.id ? 'Loading...' : 'View'}
                                </button>
                              ) : (
                                <span>{item.status === 'completed' || item.status === 'failed' ? 'Pending' : 'N/A'}</span>
                              )}
                              {isCourtCase && (item.status === 'in_progress' || item.status === 'pending') && (
                                <button
                                  type="button"
                                  className="kyc-link-btn"
                                  onClick={() => handleFetchVerificationResult(item.id)}
                                  disabled={fetchLoadingId === item.id}
                                >
                                  {fetchLoadingId === item.id ? 'Fetching...' : 'Fetch Result'}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="kyc-pagination">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  <span>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

KycGenericVerification.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  kycType: PropTypes.string.isRequired,
  costField: PropTypes.string.isRequired,
  inputs: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    label: PropTypes.string,
    type: PropTypes.string,
    placeholder: PropTypes.string,
    maxLength: PropTypes.number,
    required: PropTypes.bool,
    requiredMessage: PropTypes.string,
    validator: PropTypes.func,
    errorMessage: PropTypes.string,
    transform: PropTypes.func,
    formatter: PropTypes.func,
    autoComplete: PropTypes.string
  })).isRequired,
  submitLabel: PropTypes.string,
  referenceFormatter: PropTypes.func
};
